import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="chat-input"
export default class extends Controller {
  static targets = [
    "text",
    "submitBtn",
    "previewContainer",
    "imagePreviewWrapper",
    "imageThumbnail",
    "imageInput",
    "videoPreviewWrapper",
    "videoThumbnail",
    "videoInput",
    "audioPreviewWrapper",
    "audioInput",
    "recordingUI",
    "recordingTimer",
  ];

  connect() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
    this.timerInterval = null;
    this.recordingTime = 0;
    this.checkState(); // Set initial button state on load
  }

  // --- 1. CORE STATE MANAGEMENT ---
  checkState() {
    const hasText =
      this.hasTextTarget && this.textTarget.value.trim().length > 0;
    const hasImage =
      this.hasImageInputTarget && this.imageInputTarget.files.length > 0;
    const hasVideo =
      this.hasVideoInputTarget && this.videoInputTarget.files.length > 0;
    const hasAudio =
      this.hasAudioInputTarget && this.audioInputTarget.files.length > 0;

    // Enable submit if text OR any media type exists
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = !(
        hasText ||
        hasImage ||
        hasVideo ||
        hasAudio
      );
    }

    // Manage master preview container visibility
    if (this.hasPreviewContainerTarget) {
      if (hasImage || hasVideo || hasAudio) {
        this.previewContainerTarget.classList.remove("hidden");
      } else {
        this.previewContainerTarget.classList.add("hidden");
      }
    }
  }

  clearAll() {
    this.clearImage(false);
    this.clearVideo(false);
    this.clearAudio(false);
    setTimeout(() => this.checkState(), 10);
  }

  clearImage(updateState = true) {
    if (this.hasImageInputTarget) this.imageInputTarget.value = "";
    if (this.hasImageThumbnailTarget) this.imageThumbnailTarget.src = "";
    if (this.hasImagePreviewWrapperTarget)
      this.imagePreviewWrapperTarget.classList.add("hidden");
    if (updateState) this.checkState();
  }

  clearVideo(updateState = true) {
    if (this.hasVideoInputTarget) this.videoInputTarget.value = "";
    if (this.hasVideoThumbnailTarget) this.videoThumbnailTarget.src = "";
    if (this.hasVideoPreviewWrapperTarget)
      this.videoPreviewWrapperTarget.classList.add("hidden");
    if (updateState) this.checkState();
  }

  clearAudio(updateState = true) {
    if (this.hasAudioInputTarget) this.audioInputTarget.value = "";
    if (this.hasAudioPreviewWrapperTarget)
      this.audioPreviewWrapperTarget.classList.add("hidden");

    if (this.isRecording) this.stopAudioRecording();
    if (updateState) this.checkState();
  }

  // --- 2. IMAGE PREVIEW HANDLING ---
  previewImage(event) {
    const file = event.target.files[0];
    if (file) {
      this.imageThumbnailTarget.src = URL.createObjectURL(file);
      this.imagePreviewWrapperTarget.classList.remove("hidden");
      this.checkState();
    } else {
      this.clearImage();
    }
  }

  // --- 3. VIDEO PREVIEW HANDLING ---
  previewVideo(event) {
    const file = event.target.files[0];
    if (file) {
      this.videoThumbnailTarget.src = URL.createObjectURL(file);
      this.videoPreviewWrapperTarget.classList.remove("hidden");
      this.checkState();
    } else {
      this.clearVideo();
    }
  }

  // --- 4. AUDIO RECORDING ---
  toggleAudioRecording(event) {
    event.preventDefault();
    if (this.isRecording) {
      this.stopAudioRecording();
    } else {
      this.startAudioRecording();
    }
  }

  async startAudioRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream);

      this.mediaRecorder.ondataavailable = (e) => {
        this.audioChunks.push(e.data);
      };

      this.mediaRecorder.start();
      this.isRecording = true;

      if (this.hasRecordingUITarget) {
        this.recordingUITarget.classList.remove("hidden");
      }

      this.recordingTime = 0;
      this.updateTimerDisplay();

      this.timerInterval = setInterval(() => {
        this.recordingTime++;
        this.updateTimerDisplay();
      }, 1000);
    } catch (err) {
      console.error("Microphone access denied", err);
      alert("Please allow microphone permissions to send voice notes.");
    }
  }

  stopAudioRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: "audio/webm" });
        const file = new File([audioBlob], "voice_note.webm", {
          type: "audio/webm",
        });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        this.audioInputTarget.files = dataTransfer.files;

        this.audioPreviewWrapperTarget.classList.remove("hidden");
        this.checkState();

        this.audioChunks = [];
      };
      this.mediaRecorder.stop();
      this.isRecording = false;

      if (this.hasRecordingUITarget) {
        this.recordingUITarget.classList.add("hidden");
      }
      clearInterval(this.timerInterval);
    }
  }

  updateTimerDisplay() {
    if (!this.hasRecordingTimerTarget) return;
    const mins = Math.floor(this.recordingTime / 60)
      .toString()
      .padStart(2, "0");
    const secs = (this.recordingTime % 60).toString().padStart(2, "0");
    this.recordingTimerTarget.textContent = `${mins}:${secs}`;
  }
}
